import { createTheme, darken, responsiveFontSizes } from '@mui/material';
import { deepOrange, green, lightBlue, red } from '@mui/material/colors';
import { deepmerge } from '@mui/utils';

// Text colors for light-mode semantic surfaces (chip labels + alert text),
// kept in one place so the chip and its matching alert never drift apart.
const lightSemanticText = {
  error: '#B01332',
  warning: '#5C2100',
  success: '#0f742a',
  info: '#505f6c',
};

const baseThemeObject = {
  palette: {
    primary: {
      main: '#897BE9',
    },
    secondary: { main: '#897BE9' },
    accent: { main: '#F96068' },
    // Sidebar gradient endpoints. Defined here (not derived from
    // primary/secondary) so the navy -> purple gradient survives in dark
    // mode, where primary and secondary both collapse to purple.
    sidebar: {
      gradientTop: '#1C245A', // navy
      gradientBottom: '#523EBC', // dark purple (between navy and purple)
    },
    common: {
      blue2: '#36c7d5',
    },
    error: {
      light: '#FEF0EE',
      main: red[400],
    },
    warning: {
      light: '#FFEEE5',
      main: deepOrange[200],
    },
    success: {
      light: '#e5f3e0',
      main: green[400],
    },
    info: {
      light: '#f4f9ff',
      main: lightBlue[400],
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        margin: 'normal' as const,
        size: 'small' as const,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained' as const,
      },
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
        },
        loadingIndicator: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
      variants: [
        {
          props: { size: 'extraSmall' as 'extraSmall' },
          style: { fontSize: 8, minWidth: 'min-content', padding: '4px 8px' },
        },
        /**
         * A dashed placeholder affordance: reads as the empty slot for the thing
         * it creates rather than as a solid control. Used for "add another row"
         * buttons and for drop targets, so both share one definition.
         */
        {
          props: { variant: 'dashed' as 'dashed' },
          style: ({ theme }) => ({
            border: `1px dashed ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:hover': {
              border: `1px dashed ${theme.palette.primary.main}`,
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.primary.main,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              border: `1px dashed ${theme.palette.action.disabledBackground}`,
              color: theme.palette.text.disabled,
            },
          }),
        },
      ],
    },
    MuiCircularProgress: {
      defaultProps: {
        color: 'primary' as const,
      },
    },
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        filterFormDeleteIcon: {
          width: 'auto',
        },
        filterFormValueInput: {
          '&& .MuiFormControl-root': {
            margin: 0,
            '.MuiInput-input': {
              marginTop: '4px',
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        component: 'div',
      },
      styleOverrides: {
        root: {
          marginTop: 0,
          '&>div:first-of-type': {
            marginTop: 4,
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        ChipProps: {
          'data-testid': 'AutocompleteField.Chip',
          color: 'primary' as const,
        },
        size: 'small' as const,
      },
    },
    MuiTypography: {
      styleOverrides: {
        h2: ({ theme }) =>
          theme.unstable_sx({
            mb: 2,
          }),
      },
    },
    MuiToggleButtonGroup: {
      defaultProps: {
        size: 'small' as const,
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
          },
          '&.Mui-selected:hover': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
          },
          borderColor: theme.palette.primary.light,
          color: theme.palette.primary.light,
          textTransform: 'none' as const,
        }),
      },
      variants: [
        {
          props: { size: 'extraSmall' as 'extraSmall' },
          style: { fontSize: 8, padding: '4px 8px' },
        },
      ],
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontWeight: 'bold',
          fontSize: 12,
        }),
        root: ({ theme }) => ({
          '&.Mui-error': {
            '.MuiFormControlLabel-label': {
              color: theme.palette.error.main,
            },
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          ':where(:not(.MuiInputLabel-root))': {
            color: theme.palette.text.primary,
            fontWeight: 'bold',
            marginBottom: 5,
            fontSize: 12,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize' as const,
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            color: darken(theme.palette.primary.main, 0.1),
          },
        }),
      },
    },
  },
  typography: {
    fontFamily: '"montserrat", "lato", "Arial", sans-serif',
    fontSize: 14,
    body1: {
      fontSize: 14,
    },
    body2: {
      fontSize: 14,
    },
    body2Bold: {
      fontSize: 14,
      fontWeight: 700,
    },
    body3: {
      fontSize: 12,
    },
    body4: {
      fontSize: 10,
      fontWeight: 500,
    },
    h1: {
      fontFamily: '"Anton", "montserrat" ,"Arial", sans-serif',
      fontSize: 24,
      fontWeight: 400,
      letterSpacing: '0.7px',
    },
    cardValue: {
      fontFamily: '"montserrat", "Arial", sans-serif',
      fontSize: 24,
      fontWeight: 700,
    },
    h2: {
      fontSize: 22,
      fontWeight: 700,
    },
    h3: {
      fontSize: 18,
      fontWeight: 700,
    },
    h4: {
      fontSize: 16,
      fontWeight: 700,
    },
    h5: {
      fontSize: 14,
      fontWeight: 700,
    },
    h6: {
      fontSize: 12,
      fontWeight: 700,
    },
    logo: {
      fontFamily: '"montserrat" ,"Arial", sans-serif',
      fontSize: 24,
      fontWeight: 500,
    },
    tableHeader: {
      fontSize: 14,
      fontWeight: 700,
      lineHeight: '18px',
      letterSpacing: '0.17px',
    },
  },
  forms: {
    maxWidth: 1000,
  },
};

const makeLightAlert =
  (key, color) =>
  ({ theme }) => ({
    backgroundColor: theme.palette[key].light,
    color,
    '>.MuiAlert-icon': {
      color: theme.palette[key].main,
    },
    '.MuiAlert-root': {
      borderWidth: '1px',
      borderStyle: 'solid',
    },
  });

export const lightTheme = responsiveFontSizes(
  createTheme(
    deepmerge(baseThemeObject, {
      palette: {
        primary: {
          main: '#1C245A',
        },
        secondary: { main: '#6346DC' },
        mode: 'light',
        background: {
          default: '#f5f7f9',
        },
      },
      components: {
        MuiTypography: {
          styleOverrides: {
            h1: ({ theme }) => ({
              color: theme.palette.primary.main,
            }),
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: 'white' as const,
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            colorError: ({ theme }) => ({
              backgroundColor: theme.palette.error.light,
              color: lightSemanticText.error,
            }),
            colorWarning: ({ theme }) => ({
              backgroundColor: theme.palette.warning.light,
              color: lightSemanticText.warning,
            }),
            colorSuccess: ({ theme }) => ({
              backgroundColor: theme.palette.success.light,
              color: lightSemanticText.success,
            }),
            colorInfo: ({ theme }) => ({
              backgroundColor: theme.palette.info.light,
              color: lightSemanticText.info,
            }),
          },
        },
        MuiAlert: {
          styleOverrides: {
            standardInfo: makeLightAlert('info', lightSemanticText.info),
            standardError: makeLightAlert('error', lightSemanticText.error),
            standardSuccess: makeLightAlert(
              'success',
              lightSemanticText.success,
            ),
            standardWarning: makeLightAlert(
              'warning',
              lightSemanticText.warning,
            ),
          },
        },
      },
    }),
  ),
);

const makeDarkAlert =
  key =>
  ({ theme }) => ({
    backgroundColor: darken(theme.palette[key].dark, 0.8),
    color: theme.palette[key].contrastText,
    '.MuiAlert-icon': {
      color: theme.palette[key].main,
    },
    borderColor: theme.palette[key].main,
  });

export const darkTheme = responsiveFontSizes(
  createTheme(
    deepmerge(baseThemeObject, {
      palette: {
        mode: 'dark',
        background: {
          default: '#222',
          paper: '#060606',
        },
        sidebar: {
          gradientTop: '#4833A1', // dark purple (between purple and navy)
          gradientBottom: '#2C3781', // navy
        },
        error: {
          contrastText: baseThemeObject.palette.error.light,
        },
        warning: {
          contrastText: baseThemeObject.palette.warning.light,
        },
        success: {
          contrastText: baseThemeObject.palette.success.light,
        },
        info: {
          contrastText: baseThemeObject.palette.info.light,
        },
      },
      components: {
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: '#333' as const,
            },
          },
        },
        MuiChip: {
          defaultProps: {
            variant: 'outlined' as const,
          },
          styleOverrides: {
            colorError: ({ theme }) => ({
              color: theme.palette.error.main,
            }),
            colorWarning: ({ theme }) => ({
              color: theme.palette.warning.main,
            }),
            colorSuccess: ({ theme }) => ({
              color: theme.palette.success.main,
            }),
            colorInfo: ({ theme }) => ({
              color: theme.palette.info.main,
            }),
          },
        },
        MuiAutocomplete: {
          defaultProps: {
            ChipProps: {
              color: 'primary' as const,
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            standardInfo: makeDarkAlert('info'),
            standardError: makeDarkAlert('error'),
            standardSuccess: makeDarkAlert('success'),
            standardWarning: makeDarkAlert('warning'),
          },
        },
      },
    }),
  ),
);
